import { Button, Center, Container, Group, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconArrowLeft, IconLogin2, IconUser } from '@tabler/icons-react';
import { type SubmitEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { roomNameFromPath } from '@/app/route-paths';
import { LanguageSwitcher } from '@/shared/language-switcher';
import { notifyError } from '@/shared/notify';

export function SignInPage() {
    const { t } = useTranslation('auth');
    const [guestMode, setGuestMode] = useState(false);
    const [name, setName] = useState('');
    const [nameMissing, setNameMissing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const returnTo = location.pathname + location.search;
    const roomName = roomNameFromPath(location.pathname);
    const guestsAllowed = useGuestsAllowed(roomName);

    useEffect(() => {
        if (guestMode) nameInputRef.current?.focus();
    }, [guestMode]);

    const joinAsGuest = (event: SubmitEvent) => {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameMissing(true);
            return;
        }
        setSubmitting(true);
        fetch('/api/auth/guest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: trimmedName, roomName }),
        })
            .then(async (response) => {
                if (!response.ok) throw new Error(await response.text());
                location.reload();
            })
            .catch((error: unknown) => {
                setSubmitting(false);
                notifyError(t('signIn.error'), error);
            });
    };

    return (
        <Center mih="100dvh" p="md">
            <Container p={0} size="xs" w="100%">
                <Paper withBorder p="xl">
                    <Stack gap="lg">
                        <Group justify="space-between" wrap="nowrap">
                            <Title order={2}>{t('signIn.title')}</Title>
                            <LanguageSwitcher />
                        </Group>
                        <Text c="dimmed">{guestsAllowed ? t('signIn.description') : t('signIn.descriptionEmployees')}</Text>
                        {guestMode ? (
                            <Stack component="form" gap="md" onSubmit={joinAsGuest}>
                                <TextInput
                                    ref={nameInputRef}
                                    autoComplete="name"
                                    error={nameMissing && !name.trim() ? t('signIn.nameRequired') : undefined}
                                    label={t('signIn.nameLabel')}
                                    maxLength={64}
                                    placeholder={t('signIn.namePlaceholder')}
                                    value={name}
                                    onChange={(event) => {
                                        setName(event.currentTarget.value);
                                    }}
                                />
                                <Button loading={submitting} type="submit" variant="filled">
                                    {t('signIn.continue')}
                                </Button>
                                <Button
                                    leftSection={<IconArrowLeft size={20} />}
                                    variant="subtle"
                                    onClick={() => {
                                        setGuestMode(false);
                                    }}
                                >
                                    {t('signIn.back')}
                                </Button>
                            </Stack>
                        ) : (
                            <Stack gap="md">
                                <Button
                                    component="a"
                                    href={`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`}
                                    leftSection={<IconLogin2 size={20} />}
                                    variant="filled"
                                >
                                    {t('signIn.smallId')}
                                </Button>
                                {guestsAllowed && (
                                    <Button
                                        leftSection={<IconUser size={20} />}
                                        variant="light"
                                        onClick={() => {
                                            setGuestMode(true);
                                        }}
                                    >
                                        {t('signIn.guest')}
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Center>
    );
}

function useGuestsAllowed(roomName: string | null) {
    const [allowed, setAllowed] = useState(false);

    useEffect(() => {
        if (!roomName) return;
        const controller = new AbortController();
        fetch(`/api/meetings/${encodeURIComponent(roomName)}/guest-access`, { signal: controller.signal })
            .then((response) => (response.ok ? (response.json() as Promise<{ guests: boolean }>) : { guests: false }))
            .then(({ guests }) => {
                setAllowed(guests);
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) console.error(error);
            });
        return () => {
            controller.abort();
        };
    }, [roomName]);

    return allowed;
}
